import { useQuery } from '@tanstack/react-query'
import { type GreeksReport } from '@greencandle/api-client'
import { api } from '../../api'
import { Failed, num, toneOf } from '../primitives'

/**
 * QNT-4 / UI-G1: netted option greeks across the book.
 */

/**
 * The net-greeks strip above the strategy cards.
 *
 * <p>The cards below show each construction on its own; this says what the desk is, in total.
 * Read leg by leg an option book looks like forty unrelated bets - netted, it is a direction and
 * a daily cost, which are the two things worth knowing before opening another one.
 *
 * <p>Delta is in shares of the underlying rather than as a raw 0.62, because a raw delta is not
 * a position until it is multiplied by contract size and count. Theta is money per day.
 */
export function GreeksStrip({ accountId }: { accountId: string }) {
  const greeks = useQuery({
    queryKey: ['greeks', accountId],
    queryFn: () => api.greeks(accountId),
    // The same minute-scale refresh the quotes use: greeks move with the underlying.
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  })

  const report: GreeksReport | undefined = greeks.data

  if (greeks.isError) return <Failed error={greeks.error} />
  if (!report) return <p className="panel-foot">Loading…</p>

  if (!report.available) {
    return (
      <div className="panel-gap">
        <span className="badge badge-custom">not available</span>
        <p>{report.reason}</p>
      </div>
    )
  }

  return (
    <>
      <div className="risk-figures">
        <div className="risk-figure">
          <span className="label">Net delta</span>
          <span className={`value ${toneOf(report.delta)}`}>
            {num(report.delta)}
          </span>
          <span className="cell-sub">shares of underlying</span>
        </div>
        <div className="risk-figure">
          <span className="label">Daily theta</span>
          <span className={`value ${toneOf(report.theta)}`}>
            {num(report.theta)}
          </span>
          <span className="cell-sub">per day, all else equal</span>
        </div>
        <div className="risk-figure">
          <span className="label">Net gamma</span>
          <span className="value">{num(report.gamma)}</span>
          <span className="cell-sub">delta gained per 1 point</span>
        </div>
        <div className="risk-figure">
          <span className="label">Net vega</span>
          <span className="value">{num(report.vega)}</span>
          <span className="cell-sub">per 1 vol point</span>
        </div>
      </div>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Underlying</th>
              <th className="right">Net delta</th>
              <th className="right">From options</th>
              <th className="right">From shares</th>
              <th className="right">Theta / day</th>
              <th className="right">Vega</th>
              <th className="right">Contracts</th>
            </tr>
          </thead>
          <tbody>
            {report.byUnderlying.map((row) => (
              <tr key={row.underlying}>
                <td>
                  <strong>{row.underlying}</strong>
                </td>
                <td className={`right num ${toneOf(row.delta)}`}>
                  <strong>{num(row.delta)}</strong>
                </td>
                <td className="right num">{num(row.optionDelta)}</td>
                <td className="right num">{num(row.shareDelta)}</td>
                <td className={`right num ${toneOf(row.theta)}`}>
                  {num(row.theta)}
                </td>
                <td className="right num">{num(row.vega)}</td>
                <td className="right num">{row.contracts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {report.unpriced.length > 0 && (
        <p className="panel-foot warn">
          No greeks for {report.unpriced.join(', ')} — expired or unquoted. They are left out of
          these totals rather than counted as flat.
        </p>
      )}
      <p className="panel-foot">
        Netted from {report.source} as of{' '}
        {report.asOf ? new Date(report.asOf).toLocaleString() : '—'}. Delta is in shares of the
        underlying, so a written call reduces it: 100 shares against one short 0.40 call is 60,
        not 140.
      </p>
    </>
  )
}
